import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import TopBar from '../components/TopBar';
import { useLang } from '../LangContext';
import { useT } from '../i18n';
import { getMember, redeem } from '../api';

const REDEEM_COST = 50;

export default function RedemptionConfirmScreen() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const mode = params.get('mode') || 'points'; // 'points' | 'token'
  const { lang } = useLang();
  const t = useT(lang);

  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getMember().then(setMember).catch(console.error);
  }, []);

  const currentPoints = member?.points ?? 0;
  const currentTokens = member?.tokens ?? 0;

  const handleConfirm = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const result = await redeem(mode);
      if (!result.success) {
        if (result.reason === 'insufficient_points') {
          navigate('/error?type=insufficient');
        } else if (result.reason === 'no_tokens') {
          navigate('/error?type=insufficient');
        } else {
          navigate('/error?type=general');
        }
        return;
      }
      // 成功 → 帶 UUID 到條碼畫面
      navigate('/barcode', {
        state: {
          uuid: result.barcodeToken,
          remainingSeconds: result.remainingSeconds || 300,
          memberName: member?.name || '',
          mode,
        },
      });
    } catch (err) {
      console.error(err);
      navigate('/error?type=general');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      backgroundColor: '#FFF5EE',
      minHeight: '100dvh',
      paddingTop: 56,
      paddingBottom: 160,
      fontFamily: "'Be Vietnam Pro', sans-serif",
    }}>
      <TopBar showBack title={t('confirmTitle')} />

      <div style={{ padding: '20px 24px', maxWidth: 480, margin: '0 auto' }}>

        {/* ── Mode Selector ───────────────────────── */}
        <div style={{
          display: 'flex', gap: 4, padding: 4,
          backgroundColor: '#f2efff',
          borderRadius: 9999, marginBottom: 28,
        }}>
          {[
            { value: 'points', label: t('pointsMode') },
            { value: 'token',  label: t('tokenMode') },
          ].map(({ value, label }) => {
            const active = mode === value;
            return (
              <button
                key={value}
                onClick={() => navigate(`/confirm?mode=${value}`, { replace: true })}
                style={{
                  flex: 1, height: 44,
                  borderRadius: 9999,
                  border: 'none',
                  backgroundColor: active ? '#FF5C36' : 'transparent',
                  color: active ? '#ffffff' : '#5a5971',
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontWeight: active ? 700 : 500,
                  fontSize: 13,
                  cursor: 'pointer',
                  boxShadow: active ? '0 2px 8px rgba(255,92,54,0.2)' : 'none',
                  transition: 'all 0.2s',
                }}
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* ── Item Summary Card ───────────────────── */}
        <div style={{
          backgroundColor: '#ffffff',
          borderRadius: 20,
          padding: 24,
          boxShadow: '0px 12px 32px rgba(45,45,66,0.06)',
          marginBottom: 20,
          position: 'relative',
          overflow: 'hidden',
        }}>
          {/* Decorative blur */}
          <div style={{
            position: 'absolute', top: -48, right: -48,
            width: 128, height: 128,
            backgroundColor: 'rgba(249,204,97,0.1)',
            borderRadius: '50%', filter: 'blur(32px)',
          }}/>

          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            textAlign: 'center', position: 'relative', zIndex: 1,
          }}>
            {/* Icon circle */}
            <div style={{
              width: 80, height: 80,
              backgroundColor: '#f9cc61',
              borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: 16,
              boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.08)',
            }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="#b02600">
                <path d="M20 6h-2.18c.07-.44.18-.88.18-1.36C18 2.07 15.93 0 13.36 0c-1.31 0-2.4.56-3.27 1.44L9 2.9 7.91 1.44C7.04.56 5.95 0 4.64 0 2.07 0 0 2.07 0 4.64c0 .48.11.92.18 1.36H0c-1.1 0-2 .9-2 2v13c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2z"/>
              </svg>
            </div>

            <h2 style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 800, fontSize: 20, color: '#1A1A2E', marginBottom: 28,
            }}>{t('gachaName')}</h2>

            {/* Cost rows */}
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 0 }}>
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '12px 0',
                borderBottom: '1px solid #f2efff',
              }}>
                <span style={{ color: '#5a5971', fontWeight: 500, fontSize: 14 }}>
                  {t('deductPoints')}
                </span>
                <span style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontWeight: 800, fontSize: 18, color: '#FF5C36',
                }}>
                  {mode === 'token' ? '0 點' : `${REDEEM_COST} 點`}
                </span>
              </div>
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '12px 0',
              }}>
                <span style={{ color: '#5a5971', fontWeight: 500, fontSize: 14 }}>
                  {t('balanceAfter')}
                </span>
                <span style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontWeight: 800, fontSize: 18, color: '#1A1A2E',
                }}>
                  {member === null
                    ? '—'
                    : mode === 'token'
                      ? `${currentPoints.toLocaleString()} 點`
                      : `${(currentPoints - REDEEM_COST).toLocaleString()} 點`}
                </span>
              </div>
              {mode === 'token' && (
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '12px 0',
                  borderTop: '1px solid #f2efff',
                }}>
                  <span style={{ color: '#5a5971', fontWeight: 500, fontSize: 14 }}>
                    活動資格餘額
                  </span>
                  <span style={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontWeight: 800, fontSize: 18, color: '#1A1A2E',
                  }}>
                    {member === null ? '—' : currentTokens > 0 ? `${currentTokens - 1} 次` : '0 次'}
                  </span>
                </div>
              )}
            </div>

            {/* Note */}
            <div style={{
              marginTop: 24,
              paddingTop: 16,
              borderTop: '1px dashed rgba(172,170,197,0.3)',
              width: '100%',
            }}>
              <p style={{ fontSize: 11, color: '#5a5971', fontStyle: 'italic', lineHeight: 1.7 }}>
                {t('barcodeNote')}
              </p>
            </div>
          </div>
        </div>

        {/* ── Warning Box ─────────────────────────── */}
        <div style={{
          backgroundColor: '#FFF8E6',
          borderLeft: '4px solid #f9cc61',
          borderRadius: '0 12px 12px 0',
          padding: 16,
          display: 'flex', gap: 12,
          marginBottom: 28,
        }}>
          <div style={{ flexShrink: 0, marginTop: 1 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="#745700">
              <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
            </svg>
          </div>
          <div>
            <h3 style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 700, fontSize: 13, color: '#745700', marginBottom: 4,
            }}>{t('warningTitle')}</h3>
            <p style={{ fontSize: 11, color: '#664c00', lineHeight: 1.7 }}>
              {t('warningDesc')}
            </p>
          </div>
        </div>

        {/* ── Context image placeholder ────────────── */}
        <div style={{
          borderRadius: 24, overflow: 'hidden',
          aspectRatio: '16/9',
          position: 'relative',
          background: 'linear-gradient(135deg, #2d2d42 0%, #1A1A2E 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{ textAlign: 'center', opacity: 0.4 }}>
            <svg width="64" height="64" viewBox="0 0 24 24" fill="#ffffff">
              <path d="M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .55.22 1.05.59 1.42l9 9c.36.36.86.58 1.41.58.55 0 1.05-.22 1.41-.59l7-7c.37-.36.59-.86.59-1.41 0-.55-.23-1.06-.59-1.42zM5.5 7C4.67 7 4 6.33 4 5.5S4.67 4 5.5 4 7 4.67 7 5.5 6.33 7 5.5 7z"/>
            </svg>
            <div style={{ color: '#ffffff', fontSize: 12, marginTop: 8, fontWeight: 500 }}>
              雙層扭蛋機
            </div>
          </div>
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0, height: '50%',
            background: 'linear-gradient(to top, #FFF5EE, transparent)',
          }}/>
        </div>

      </div>

      {/* ── Sticky Bottom Actions ───────────────── */}
      <footer style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        maxWidth: 390, margin: '0 auto',
        backgroundColor: '#ffffff',
        padding: '16px 24px 32px',
        boxShadow: '0px -12px 32px rgba(45,45,66,0.06)',
        borderRadius: '24px 24px 0 0',
        zIndex: 40,
      }}>
        <div style={{ maxWidth: 480, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button
            onClick={handleConfirm}
            disabled={loading || member === null || (mode === 'token' && currentTokens < 1) || (mode === 'points' && currentPoints < REDEEM_COST)}
            style={{
              height: 52, width: '100%',
              backgroundColor: (loading || member === null || (mode === 'token' && currentTokens < 1) || (mode === 'points' && currentPoints < REDEEM_COST)) ? '#cccccc' : '#FF5C36',
              color: '#ffffff',
              border: 'none', borderRadius: 12,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 700, fontSize: 15,
              cursor: (loading || member === null || (mode === 'token' && currentTokens < 1) || (mode === 'points' && currentPoints < REDEEM_COST)) ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              boxShadow: '0 4px 20px rgba(255,92,54,0.2)',
              transition: 'transform 0.15s',
            }}
          >
            {loading ? '處理中...' : t('confirmCta')}
            {!loading && (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 11h4v2H3v-2zm4-4H5V5H3v2h4V5h2v2H7zM3 7h2V5H3v2zm8 0h2V5h-2v2zm-4 0h2V5H7v2zm8 4h-2v2h2v-2zm-4-4v2h2V7h-2zm4 0h-2v2h2V7zM3 17h4v2H3v-2zm4-4H3v2h4v2h2v-4H7zm2 4h2v-2H9v2zm4-4h-2v4h4v-2h-2v-2zm-4-2h2v-2H9v2z"/>
              </svg>
            )}
          </button>
          <button
            onClick={() => navigate('/')}
            style={{
              height: 52, width: '100%',
              backgroundColor: 'transparent',
              border: '2px solid #FF5C36', color: '#FF5C36',
              borderRadius: 12,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 700, fontSize: 15, cursor: 'pointer',
              transition: 'transform 0.15s',
            }}
          >
            {t('cancel')}
          </button>
        </div>
      </footer>
    </div>
  );
}
